import React, { useState } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { SecuredFooter } from "./ui/secured-footer";
import addressPng from "../assets/images/settings/address_book.png";
import {
  Pencil,
  Trash2,
  Copy,
  Loader2,
  QrCode,
  Mail,
  Phone,
  Info,
} from "lucide-react";
import { useToast } from "../hooks/use-toast";
import {
  deletePayAddress,
  deleteWalletAddress,
} from "../services/settingsService";
import DeletePayIDConfirmModal from "./DeletePayIDConfirmModal";

const AddressContact = ({
  setPageNo,
  updateUser,
  setAction,
  setUpdateUser,
  setRefresh,
  type,
}) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text || "");
    toast({
      description: "Copied to clipboard",
      variant: "eighty",
    });
  };

  async function deletePayAddressCall() {
    setIsLoading(true);
    try {
      const apiCall = await deletePayAddress(updateUser?._id);
      if (apiCall.status === 200) {
        setIsLoading(false);
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
      }
    } catch (err) {
      setIsLoading(false);
      setShowDeleteConfirm(false);
      toast({
        description: "Failed to delete address",
        variant: "destructive",
      });
    }
  }

  async function deleteWalletAddressCall() {
    setIsLoading(true);
    try {
      const apiCall = await deleteWalletAddress(updateUser?._id);
      if (apiCall.status === 200) {
        setIsLoading(false);
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
      }
    } catch (err) {
      setIsLoading(false);
      setShowDeleteConfirm(false);
      toast({
        description: "Failed to delete address",
        variant: "destructive",
      });
    }
  }

  const handleEdit = () => {
    setUpdateUser(updateUser);
    setAction("update");
    if (type === "Crypto") {
      setPageNo(1);
    } else {
      setPageNo(2);
    }
  };

  const handleDelete = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    if (type === "Crypto") {
      deleteWalletAddressCall();
    } else {
      deletePayAddressCall();
    }
  };

  const handleDoneAfterDelete = () => {
    setRefresh(true);
    setPageNo(0);
  };

  // Auto-detect payment method type for the modal
  const getDeleteType = () => {
    if (type === "Crypto") return "wallet";
    return detectPaymentType(updateUser?.payId);
  };

  // Auto-detect payment method type from payId value
  const detectPaymentType = (payId) => {
    if (!payId) return "payid";
    // Email: contains @
    if (payId.includes("@")) return "email";
    // Phone: starts with + or is all digits (and longer than 7 chars)
    if (/^\+?\d{7,}$/.test(payId)) return "phone";
    // Default: PayID
    return "payid";
  };

  // Get payment method icon based on payId value (auto-detect type)
  const getPaymentMethodIcon = (payId) => {
    const type = detectPaymentType(payId);
    switch (type) {
      case "email":
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#57249C] to-[#EB3366] flex items-center justify-center">
            <Mail className="w-4 h-4 text-white" />
          </div>
        );
      case "phone":
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#57249C] to-[#EB3366] flex items-center justify-center">
            <Phone className="w-4 h-4 text-white" />
          </div>
        );
      default: // payid
        return (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#57249C] to-[#EB3366] flex items-center justify-center">
            <QrCode className="w-4 h-4 text-white" />
          </div>
        );
    }
  };

  // Get payment method label based on payId value
  const getPaymentMethodLabel = (payId) => {
    const type = detectPaymentType(payId);
    switch (type) {
      case "email":
        return "Email";
      case "phone":
        return "Cellphone number";
      default:
        return "PayID";
    }
  };

  // Delete Success Screen
  if (showDeleteSuccess) {
    return (
      <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-hidden">
        <CardContent className="p-0">
          {/* Success Banner */}
          <div className="px-8 pt-8">
            <div className="bg-[#EFF1FC] rounded-2xl px-6 py-4 flex items-center justify-center gap-2">
              <Info className="w-4 h-4 text-[#6375E6] flex-shrink-0" />
              <p className="text-[14px] text-[#6375E6] font-normal">
                {type === "Crypto"
                  ? "Wallet address"
                  : detectPaymentType(updateUser?.payId) === "email"
                    ? "Email"
                    : detectPaymentType(updateUser?.payId) === "phone"
                      ? "Phone number"
                      : "PayID"}{" "}
                successfully deleted
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center py-16 px-8">
            <div className="w-[104px] h-[104px] rounded-full border-[3px] border-[#10B981] flex items-center justify-center mb-8">
              <svg
                className="w-10 h-10 text-[#10B981]"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <h2 className="text-[32px] font-semibold text-[#33125F] mb-6 text-center leading-[40px]">
              Successfully Deleted
            </h2>
            <p className="text-[20px] text-[#423E51] text-center mb-10 leading-[32px] max-w-[400px]">
              {type === "Pay"
                ? updateUser?.category === "Corporate"
                  ? `${updateUser?.name || ""}'s`
                  : `${updateUser?.name || ""} ${updateUser?.lastname || ""}`.trim() +
                    "'s"
                : updateUser?.category === "Corporate"
                  ? `${updateUser?.firstName || ""}'s`
                  : `${updateUser?.firstName || ""} ${updateUser?.lastName || ""}`.trim() +
                    "'s"}{" "}
              {type === "Crypto" ? "wallet address" : "payment method"} has been
              removed from your address book.
            </p>
            <Button
              onClick={handleDoneAfterDelete}
              className="h-[48px] w-[302px] rounded-full text-[14px] font-semibold text-white"
              style={{
                background:
                  "linear-gradient(69.77deg, #57249C 10.66%, #EB3366 113.47%)",
              }}
            >
              Done
            </Button>
          </div>
          <SecuredFooter />
        </CardContent>
      </Card>
    );
  }

  // Render Pay type (88Pay) - New Figma design
  if (type === "Pay") {
    return (
      <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-hidden">
        <CardContent className="p-0">
          {/* Header */}
          <div className="px-8 pt-8 pb-6">
            <div className="flex items-center justify-between">
              <h2 className="text-[20px] font-semibold text-[#6D6584]">
                {updateUser?.name} {updateUser?.lastname}
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleEdit}
                  className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
                >
                  <Pencil className="w-3 h-3 text-[#6D6584]" />
                </button>
                <button
                  onClick={handleDelete}
                  disabled={isLoading}
                  className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
                >
                  {isLoading ? (
                    <Loader2 className="w-3 h-3 text-[#6D6584] animate-spin" />
                  ) : (
                    <Trash2 className="w-3 h-3 text-[#6D6584]" />
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Payment Methods Section */}
          <div className="px-8 pb-6">
            <p className="text-[14px] font-semibold text-[#9D96AF] mb-6">
              Payment methods
            </p>

            {/* Payment Method Card */}
            <div className="border border-[#EBE9EF] rounded-2xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                {getPaymentMethodIcon(updateUser?.payId)}
                <div>
                  <p className="text-[16px] font-semibold text-[#423E51]">
                    {getPaymentMethodLabel(updateUser?.payId)}
                  </p>
                  <p className="text-[14px] text-[#423E51]">
                    {updateUser?.payId}
                  </p>
                </div>
              </div>
              <button
                onClick={() => copyToClipboard(updateUser?.payId)}
                className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
              >
                <Copy className="w-3 h-3 text-[#6D6584]" />
              </button>
            </div>
          </div>

          {/* Back Button */}
          <div className="px-8 pb-8 flex justify-center">
            <Button
              onClick={() => {
                setPageNo(0);
              }}
              className="h-[48px] px-12 rounded-full text-[14px] font-semibold bg-white border border-[#EBE9EF] text-[#6D6584] hover:bg-[#F5F3F8] transition-colors"
            >
              Back
            </Button>
          </div>

          <SecuredFooter />

          {/* Delete Confirmation Modal */}
          <DeletePayIDConfirmModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            onConfirm={confirmDelete}
            isDeleting={isLoading}
            type={getDeleteType()}
          />
        </CardContent>
      </Card>
    );
  }

  // Render Crypto type (Wallet Address) - Keep existing design
  return (
    <Card className="bg-white rounded-3xl border border-[#EBE9EF] overflow-hidden">
      <CardContent className="p-0">
        {/* Header */}
        <div className="px-8 pt-8 pb-6">
          <div className="flex items-center justify-between">
            <h2 className="text-[20px] font-semibold text-[#6D6584]">
              {updateUser?.category === "Corporate"
                ? updateUser?.firstName || ""
                : `${updateUser?.firstName || ""} ${updateUser?.lastName || ""}`.trim()}
              {updateUser?.addressName && ` - ${updateUser.addressName}`}
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleEdit}
                className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
              >
                <Pencil className="w-3 h-3 text-[#6D6584]" />
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading}
                className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors"
              >
                {isLoading ? (
                  <Loader2 className="w-3 h-3 text-[#6D6584] animate-spin" />
                ) : (
                  <Trash2 className="w-3 h-3 text-[#6D6584]" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Address Details Section */}
        <div className="px-8 pb-6">
          <p className="text-[14px] font-semibold text-[#9D96AF] mb-6">
            Address Details
          </p>

          {/* Address Card */}
          <div className="border border-[#EBE9EF] rounded-2xl p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {updateUser?.assetIcon ? (
                <img
                  src={updateUser.assetIcon}
                  alt={updateUser.assetName}
                  className="w-10 h-10 rounded-full"
                />
              ) : (
                <img src={addressPng} alt="" className="w-10 h-10" />
              )}
              <div>
                <p className="text-[16px] font-semibold text-[#423E51]">
                  {updateUser?.assetName || "Wallet"}
                </p>
                <p className="text-[14px] text-[#423E51] break-all max-w-[400px]">
                  {updateUser?.walletAddress}
                </p>
              </div>
            </div>
            <button
              onClick={() => copyToClipboard(updateUser?.walletAddress)}
              className="w-10 h-10 rounded-full border border-[#EBE9EF] flex items-center justify-center hover:bg-[#F5F3F8] transition-colors flex-shrink-0"
            >
              <Copy className="w-3 h-3 text-[#6D6584]" />
            </button>
          </div>
        </div>

        {/* Back Button */}
        <div className="px-8 pb-8 flex justify-center">
          <Button
            onClick={() => {
              setPageNo(0);
            }}
            className="h-[48px] px-12 rounded-full text-[14px] font-semibold bg-white border border-[#EBE9EF] text-[#6D6584] hover:bg-[#F5F3F8] transition-colors"
          >
            Back
          </Button>
        </div>

        <SecuredFooter />

        {/* Delete Confirmation Modal */}
        <DeletePayIDConfirmModal
          isOpen={showDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
          onConfirm={confirmDelete}
          isDeleting={isLoading}
          type={getDeleteType()}
        />
      </CardContent>
    </Card>
  );
};

export default AddressContact;
